'use client';

import {
  AppBar,
  Card,
  CardContent,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { orderBy } from 'lodash';
import { useRouter } from 'next/navigation';
import { useQueryState } from 'nuqs';
import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createCart, updateCart } from 'src/api/cart';
import { useGetEvent } from 'src/api/events';
import { useGetOrderByCartId } from 'src/api/hooks/cart';
import { useSendExclusiveRequest } from 'src/api/hooks/event.request';
import { processFreeOrder } from 'src/api/order';
import { useGetShopItem } from 'src/api/shop';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
import { HeaderShadow } from 'src/layouts/_common';
import { useTranslate } from 'src/locales';
import { bgBlur } from 'src/theme/css';
import {
  Cart,
  CartItemDTO,
  CartShopItemDTO,
  EventType,
  ShopItemDTO,
  TicketItemDTO,
} from 'src/types';
import { removeStorageGetItem } from 'src/utils/storage-available';

import EventCartModal from '../event-cart-modal';
import EventStatus from '../event-status';
import EventWaitingListCartModal from '../event-waiting-list-cart-modal';
import { ExclusiveEventDialog } from '../exclusive-event';
import { useTicketCalculations } from '../useTicketCalculations';
import EventDetailsDesktopView from './event-details-desktop-view';
import EventDetailsMobileView from './event-details-mobile-view';

type Props = {
  id: string;
};

interface FormValues {
  tickets: CartItemDTO[];
  shopItems: CartShopItemDTO[];
}

const convertTicketItem = (ticketItems: CartItemDTO[]): TicketItemDTO[] =>
  ticketItems
    .filter((item) => item.quantityInCart > 0) // Filter items that have quantityInCart > 0
    .map((item) => ({
      ticketSettingId: item.id,
      quantity: item.quantityInCart,
    }));

const convertCartShopItemsToShopItems = (shopItems: CartShopItemDTO[]): ShopItemDTO[] =>
  shopItems
    .filter((item) => item.quantityInCart > 0) // Filter items that have quantityInCart > 0
    .map((item) => ({
      shopItemId: item.id,
      quantity: item.quantityInCart,
    }));

export default function EventDetailsView({ id }: Props) {
  const mdDown = useResponsive('down', 'md');
  const mdUp = useResponsive('up', 'md');
  const { enqueueSnackbar } = useSnackbar();
  const { user, loading, authenticated } = useAuthContext();
  const router = useRouter();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const offsetTop = useOffSetTop(4);
  const { t } = useTranslate('common');

  const { data: event, isLoading, error: eventError } = useGetEvent(id, user, !loading);
  const eventDate = new Date(event ? (event?.endDate ?? event?.date) : '');
  const { data: cartData } = useGetOrderByCartId(Number(event?.cartId));
  const { data: shopItems } = useGetShopItem(Number(id));
  const [promoter, _] = useQueryState('promoter');
  const [__, setOpenLogin] = useQueryState('login');

  const [utm_medium, ___] = useQueryState('utm_medium');
  const [____, setCheckoutModal] = useQueryState('checkout');

  const { mutateAsync: sendRequest } = useSendExclusiveRequest(id);

  const defaultValues = useMemo(
    () => ({
      tickets: orderBy(event?.ticketSettings, 'price', 'asc')?.map((ticket) => ({
        ...ticket,
        quantityInCart:
          cartData?.items.find((item) => item.ticketSettingId === ticket.id)?.quantity || 0,
      })),
      shopItems: shopItems?.results?.map((shopItem) => ({
        ...shopItem,
        quantityInCart:
          cartData?.shopItems?.find((item) => item.shopItemId === shopItem.id)?.quantity || 0,
      })),
    }),
    [event?.ticketSettings, shopItems?.results, cartData?.items, cartData?.shopItems]
  );

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const { handleSubmit, reset, watch, getValues } = methods;

  useEffect(() => {
    if (event && shopItems) {
      reset(defaultValues);
    }
  }, [cartData, event, defaultValues, shopItems, reset]);

  const onNavigateBack = () => {
    if (window.history?.length) {
      router.back();
    } else {
      router.push('/');
    }
  };

  const tickets = watch('tickets') as CartItemDTO[];

  const { hasTicketWithQuantityMoreThanZero, promoCode } = useTicketCalculations({
    tickets,
    getValues,
    eventId: Number(id),
  });

  const onRoutingToCart = async (cart: Cart, paymentIntent?: string, clientSecretId?: string) => {
    try {
      // Add logging to track execution
      console.log('Starting routing process for cart:', cart.id);

      if (cart.total === 0 && event?.type !== EventType.EXCLUSIVE) {
        console.log('Processing free order...');
        await processFreeOrder(cart.id, promoter ?? undefined);

        // Use window.location for direct navigation as a fallback
        console.log('Redirecting to /me/events/?type=ticket');
        removeStorageGetItem('promo_code');
        posthog.capture('free_cart_purchased', cartData);

        // Use both router and window.location to ensure navigation happens
        router.push(`/me/events/?type=ticket`);
        setTimeout(() => {
          window.location.href = `/me/events/?type=ticket`;
        }, 500); // Fallback after 500ms
      } else if (cart.total === 0 && event?.type === EventType.EXCLUSIVE) {
        console.log('Processing exclusive event...');
        await sendRequest({
          cartId: cart.id,
        });
        setCheckoutModal(null);
      } else {
        console.log('Redirecting to cart page...');
        // Ensure all parameters are defined
        const params = new URLSearchParams();
        if (id) params.append('eventId', id);
        // if (paymentIntent) params.append('paymentIntent', paymentIntent);
        // if (clientSecretId) params.append('clientSecretId', clientSecretId);
        if (utm_medium) params.append('utm_medium', utm_medium);

        const cartUrl = `/cart/${cart.id}?${params.toString()}`;
        console.log('Navigating to:', cartUrl);

        // Use both router and window.location to ensure navigation happens
        router.push(cartUrl);
        setTimeout(() => {
          window.location.href = cartUrl;
        }, 500); // Fallback after 500ms

        posthog.capture('view_cart', cartData);
      }

      setCheckoutModal(null);
      console.log('Routing complete');
    } catch (error) {
      console.error('Error during routing:', error);
      enqueueSnackbar(typeof error === 'string' ? error : 'An error occurred during checkout', {
        variant: 'error',
      });
    }
  };

  const onSubmit = handleSubmit(
    useCallback(
      async (data: FormValues) => {
        console.log('open', data);
        if (!authenticated) {
          setOpenLogin('open');
          return;
        }

        let cart;
        if (hasTicketWithQuantityMoreThanZero && !event?.cartId && authenticated) {
          cart = await createCart(
            Number(id),
            {
              items: convertTicketItem(data.tickets),
              shopItems: convertCartShopItemsToShopItems(data.shopItems),
              ...(promoCode && { promoCodeId: promoCode.id }),
            },
            promoter ?? undefined
          );
          onRoutingToCart(cart, cart.paymentIntentId, cart?.client_secret);
        } else if (event?.cartId && authenticated) {
          cart = await updateCart(
            Number(id),
            event?.cartId,
            {
              items: convertTicketItem(data.tickets),
              shopItems: convertCartShopItemsToShopItems(data.shopItems),
              ...(promoCode && { promoCodeId: promoCode.id }),
            },
            promoter ?? undefined
          );
          onRoutingToCart(cart, cart.paymentIntentId, cart?.client_secret);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        handleSubmit,
        authenticated,
        user,
        event?.type,
        event?.cartId,
        hasTicketWithQuantityMoreThanZero,
        setOpenLogin,
        id,
        promoCode,
        promoter,
      ]
    )
  );

  if (isLoading || !event) {
    return mdUp ? <EventDesktopSkeleton /> : <EventDetailsSkeleton />;
  }

  if (eventError)
    enqueueSnackbar(eventError?.message ?? t('errors.something-wrong'), { variant: 'error' });

  return mdDown ? (
    <>
      <AppBar>
        <Toolbar
          disableGutters
          sx={{
            height: {
              xs: 3,
              md: 3,
            },
            transition: theme.transitions.create(['height'], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.shorter,
            }),
            ...(offsetTop && {
              ...bgBlur({
                color: theme.palette.background.default,
              }),
              height: {
                md: 3,
              },
            }),
          }}
        >
          <Container
            sx={{
              height: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <IconButton
              onClick={() => onNavigateBack()}
              sx={{ color: offsetTop ? '#fff' : '#000' }}
            >
              <Iconify
                height="50"
                width="50"
                icon="line-md:chevron-left-circle-twotone"
                color={offsetTop ? '#fff' : '#000'}
              />
            </IconButton>
            {event && <EventStatus event={event} eventDate={eventDate} />}
          </Container>
        </Toolbar>

        {offsetTop && <HeaderShadow />}
      </AppBar>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} noValidate>
          <EventDetailsMobileView event={event} ref={containerRef} />
          <EventCartModal flatFee={event?.flatFee} onSubmit={onSubmit} />
        </form>
      </FormProvider>
      <ExclusiveEventDialog />
      <EventWaitingListCartModal />
    </>
  ) : (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} noValidate>
          <Stack
            sx={{
              pt: { xs: 10, md: '90px' },
              flexGrow: 1,
            }}
          >
            <EventDetailsDesktopView event={event} />
            <EventCartModal flatFee={event?.flatFee} onSubmit={onSubmit} />
          </Stack>
        </form>
      </FormProvider>
      <ExclusiveEventDialog />
      <EventWaitingListCartModal />
    </>
  );
}

export const EventDetailsSkeleton = () => (
  <Stack spacing={3} sx={{ width: '100%' }}>
    <Skeleton variant="rectangular" width="100%" height={300} />
    <Stack spacing={4} p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={300} height={40} />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width={200} height={24} />
      </Stack>

      <Stack spacing={2}>
        <Skeleton variant="text" width="90%" height={24} />
        <Skeleton variant="text" width="95%" height={24} />
        <Skeleton variant="text" width="88%" height={24} />
        <Skeleton variant="text" width="92%" height={24} />
      </Stack>
      <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 2, mt: 5 }} />
    </Stack>
  </Stack>
);

const EventDesktopSkeleton = () => (
  <Stack
    direction="row"
    alignItems="flex-start"
    flexGrow={1}
    width="100%"
    gap={3}
    sx={{
      pt: { xs: 10, md: '90px' },
      px: 10,
      flexGrow: 1,
    }}
  >
    <Stack width="70%">
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width="30%" height={24} />
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ width: '100%' }}>
        <Stack spacing={3} flex={1}>
          <Typography variant="h6" component="div">
            <Skeleton variant="text" width="40%" />
          </Typography>

          <Stack spacing={2}>
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="80%" />
          </Stack>

          <Stack spacing={1}>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="75%" />
            <Skeleton variant="text" width="65%" />
          </Stack>
          <Stack spacing={1}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>

    <Stack spacing={3} width="50%">
      <Card elevation={2}>
        <Skeleton
          variant="rectangular"
          height={240}
          sx={{
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        />
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" sx={{ ml: -1 }}>
                {[...Array(4)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="circular"
                    width={32}
                    height={32}
                    sx={{
                      ml: -0.5,
                      border: '2px solid white',
                    }}
                  />
                ))}
              </Stack>
              <Skeleton variant="text" width={120} />
            </Stack>
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{
                width: '100%',
                borderRadius: 1,
              }}
            />
          </Stack>
        </CardContent>
      </Card>
      <Stack spacing={1}>
        <Skeleton variant="text" width="30%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="rectangular" height={40} width="40%" sx={{ borderRadius: 1 }} />
      </Stack>
      <Stack spacing={2}>
        <Skeleton variant="text" width="40%" />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={100} />
          </Stack>
          <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
        </Stack>
      </Stack>
    </Stack>
  </Stack>
);
